<script setup lang="ts">
import { computed } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppSingleSelect } from '@libs/ui';
import type { AppSingleSelectOption } from '@libs/ui';

import { useStatsigEventsStore } from '../../core/stores';

import type { AccommodationSelectorType } from './AccommodationSelector.model';

const statsigEventsStore = useStatsigEventsStore();

interface AccommodationSelectorProps {
  selectedOption: AccommodationSelectorType;
  disabled: boolean;
  isThereSelectedAccommodation?: boolean;
}

const props = defineProps<AccommodationSelectorProps>();

interface AccommodationSelectorEmits {
  (eventName: 'toggle-accommodation', value: AccommodationSelectorType): void;
}

const emit = defineEmits<AccommodationSelectorEmits>();

const { t } = i18n.global;

type AccommodationOption = AppSingleSelectOption<AccommodationSelectorType>;

const popularAccommodationOption: AccommodationOption = {
  id: 'popularAccommodation',
  label: t('planner.recommended'),
  icon: 'bed',
};

const cheapestAccommodationOption: AccommodationOption = {
  id: 'cheapestAccommodation',
  label: t('planner.lowest_price'),
  icon: 'bed',
};

const withoutAccommodationOption: AccommodationOption = {
  id: 'noAccommodation',
  label: t('planner.without_stay'),
  icon: 'bedOutlinedCrossed',
};

const withAccommodationOption: AccommodationOption = {
  id: 'popularAccommodation',
  label: t('planner.stays'),
  icon: 'bed',
};

const options = computed<AccommodationOption[]>(() => {
  if (props.isThereSelectedAccommodation) {
    return [withAccommodationOption, withoutAccommodationOption];
  }
  return [
    popularAccommodationOption,
    cheapestAccommodationOption,
    withoutAccommodationOption,
  ];
});

const defaultSelectedOption = computed<AccommodationOption>(() =>
  props.isThereSelectedAccommodation
    ? withAccommodationOption
    : popularAccommodationOption
);

const selectedOption = computed<AccommodationOption>(
  () =>
    options.value.find((option) => option.id === props.selectedOption) ??
    defaultSelectedOption.value
);

function handleSelect(option: AccommodationOption): void {
  if (option.id === selectedOption.value.id) {
    return;
  }

  if (option.id === 'noAccommodation') {
    statsigEventsStore.logExperimentIngredientRemoved('accommodation');
  }

  emit('toggle-accommodation', option.id);
  statsigEventsStore.logTripOverviewAccommodationSelectorOpened();
}
</script>

<template>
  <AppSingleSelect
    type="primary"
    :options="options"
    :selected="selectedOption"
    :left-side-icon="selectedOption?.icon ?? undefined"
    font-variant="font-bold"
    left-side-icon-size="18"
    :disabled="props.disabled"
    @select="handleSelect"
  />
</template>
